// Content for the "Christos · Rex Gloriae" wing — the devotions of Our Lord, entered
// through the Mother who points to her Son. Quotes are short and attributed; papal
// documents identify their own editions; summaries are not verbatim quotations.
import { SAINT_QUOTATIONS } from "./saintQuotations";
import { SACRED_HEART, IMMACULATE_HEART, GOLDEN_ARROW } from "./historicalPrayers";
import { TRADITIONAL_ENGLISH, BALTIMORE_SOURCE } from "./traditionalRosary";
import { CHAPLET_LOCALES } from "./chapletLocales";
import { scriptureText, scriptureLink } from "./scripture";

const IMG = "/images/christos";

export const CHRISTOS_HUB = {
  eyebrow: "Ad Iesum per Mariam · To Jesus through Mary",
  greek: "Χριστός",
  title: "Christos",
  latin: "Rex Gloriae · King of Glory",
  lede: "Every devotion to the Mother is for the sake of the Son. At Cana, Mary tells the servants, ‘Do whatever he tells you.’ (John 2:5, RSV-CE). This is the wing of her Son: his Heart, his Mercy, his Face, his Presence, and his reign.",
  marysWord: { text: scriptureText("John 2:5"), ref: "The Mother of Jesus, at Cana · John 2:5 · RSV-CE" },
  emblem: { src: `${IMG}/christos-emblem.webp`, alt: "The emblem of the Christos wing — Christ Pantocrator, King of Glory, enthroned in a gold roundel bordered in Marian blue, giving blessing and holding the Book, inscribed IC XC · Christos Rex Gloriae · King of Glory" },
  cards: [
    { slug: "sacred-heart", title: "The Sacred Heart of Jesus", latin: "Cor Iesu Sacratissimum", image: `${IMG}/sacred-heart.webp`, blurb: "The pierced Heart, a furnace of love revealed to St. Margaret Mary." },
    { slug: "divine-mercy", title: "The Divine Mercy", latin: "Iesu, in Te confido", image: `${IMG}/divine-mercy.webp`, blurb: "The rays of blood and water, and the message given to St. Faustina." },
    { slug: "two-hearts", title: "The Two Hearts", latin: "Cor Iesu · Cor Mariae", image: `${IMG}/two-hearts.webp`, blurb: "The Sacred Heart and the Immaculate Heart, united to save souls.", flagship: true },
    { slug: "holy-face", title: "The Holy Face", latin: "Vultus Christi", image: `${IMG}/holy-face.webp`, blurb: "The face of the Man of Sorrows, honoured in reparation." },
    { slug: "eucharistic-miracles", title: "Eucharistic Miracles", latin: "Panis Angelicus", image: "/images/treasury/carlo-acutis.webp", blurb: "The Real Presence and the signs that confirm it." },
    { slug: "sovereignty", title: "Christ the King", latin: "Christus Rex", image: "/images/prayer-rope/christ-pantocrator.webp", blurb: "His reign over every mind, will, heart, and nation." },
  ],
};

export const CHRISTOS_DEVOTIONS = {
  "sacred-heart": {
    eyebrow: "The love of God made visible",
    title: "The Sacred Heart of Jesus",
    latin: "Cor Iesu Sacratissimum",
    lede: "The Heart opened by the lance on Calvary, shown to St. Margaret Mary Alacoque at Paray-le-Monial as a living furnace of love for a world grown cold.",
    image: { src: `${IMG}/sacred-heart.webp`, alt: "The Sacred Heart of Jesus", credit: "The Sacred Heart of Jesus · devotional artwork provided by the site owner" },
    history: [
      "Between 1673 and 1675, Our Lord appeared to a Visitation nun, St. Margaret Mary Alacoque, showing her his Heart encircled with thorns and crowned with a cross. He asked for a devotion of love and reparation: frequent Communion, a holy hour, and the keeping of the First Fridays.",
      "In 1956, Pope Pius XII devoted the encyclical Haurietis Aquas to this devotion, teaching that the Heart of the Incarnate Word is the natural sign of his threefold love — divine, and the love of his human soul and senses — poured out for us on the Cross.",
    ],
    graces: {
      title: "The Twelve Promises",
      note: "Traditional devotional summaries drawn from St. Margaret Mary’s writings, not a verbatim list dictated at one encounter. Read them within repentance, worthy Communion and persevering charity — never as a mechanical guarantee of salvation.",
      items: [
        "I will give them all the graces necessary in their state of life.",
        "I will establish peace in their homes.",
        "I will comfort them in all their afflictions.",
        "I will be their secure refuge in life, and above all, in death.",
        "I will bestow abundant blessings upon all their undertakings.",
        "Sinners shall find in my Heart the source and infinite ocean of mercy.",
        "Lukewarm souls shall grow fervent.",
        "Fervent souls shall quickly rise to great perfection.",
        "I will bless every place where an image of my Heart is honoured.",
        "I will give to priests the power to touch the most hardened hearts.",
        "Those who promote this devotion shall have their names written in my Heart.",
        "To those who receive Communion on nine consecutive First Fridays, I promise the grace of final perseverance.",
      ],
    },
    quotes: [
      { text: "Margaret Mary’s reported encounters call for a response of love and reparation to Christ.", author: "St. Margaret Mary Alacoque", kind: "paraphrase", source: "Haurietis Aquas (1956), §§95–97", url: "https://www.vatican.va/content/pius-xii/en/encyclicals/documents/hf_p-xii_enc_15051956_haurietis-aquas.html" },
      { text: "the natural sign and symbol of His boundless love for the human race", author: "Pope Pius XII", source: "Haurietis Aquas (1956), §22 · excerpt", url: "https://www.vatican.va/content/pius-xii/en/encyclicals/documents/hf_p-xii_enc_15051956_haurietis-aquas.html" },
    ],
    prayer: SACRED_HEART,
    feast: "Solemnity of the Sacred Heart · the Friday after Corpus Christi",
    sources: [
      { label: "Pius XII · Haurietis Aquas (1956)", url: "https://www.vatican.va/content/pius-xii/en/encyclicals/documents/hf_p-xii_enc_15051956_haurietis-aquas.html" },
      { label: "Catechism §§478, 1432, 2669", url: "https://www.vatican.va/archive/ccc_css/archive/catechism/p1s2c2a3.htm" },
    ],
  },
  "divine-mercy": {
    eyebrow: "Jesus, I trust in You",
    title: "The Divine Mercy",
    latin: "Iesu, in Te confido",
    lede: "The same pierced Heart, revealed anew to St. Faustina Kowalska between the wars: rays of pale light and red streaming from the breast of the merciful Christ — the grace of Baptism and the Blood of the Eucharist.",
    image: { src: `${IMG}/divine-mercy.webp`, alt: "The merciful Christ, rays of pale light and red streaming from his Heart, the wounds visible", credit: "The Divine Mercy · original devotional artwork commissioned for this site" },
    history: [
      "St. Faustina Kowalska (1905–1938), a Polish sister, recorded in her diary a message of trust in God's mercy and a request for an image of the merciful Christ signed 'Jesus, I trust in You,' with two rays — pale and red — flowing from his Heart.",
      "St. John Paul II canonized her in the Jubilee Year 2000 and established Divine Mercy Sunday for the whole Church on the Second Sunday of Easter. The devotion keeps the Hour of Great Mercy at three o'clock, the hour of the Lord's death.",
    ],
    graces: {
      title: "The forms of the devotion",
      items: [
        "The Image — the merciful Christ, inscribed 'Jesus, I trust in You.'",
        "The Feast — Divine Mercy Sunday, the Octave of Easter.",
        "The Chaplet of Divine Mercy, prayed on the beads of the Rosary.",
        "The Hour of Great Mercy — a pause at three o'clock.",
        "Works of mercy — mercy shown in deed, word, and prayer.",
      ],
    },
    quotes: [
      { text: "Jesus, I trust in You", author: "Traditional English inscription of the image", source: "St. Faustina, Diary, §47; Sisters of Our Lady of Mercy", url: "https://www.saint-faustina.org/image/" },
      { text: "John Paul II describes mercy as the form love takes when it encounters suffering, injustice and human frailty.", author: "St. John Paul II", kind: "paraphrase", source: "Dives in Misericordia (1980), §7", url: "https://www.vatican.va/content/john-paul-ii/en/encyclicals/documents/hf_jp-ii_enc_30111980_dives-in-misericordia.html" },
    ],
    prayer: { id: "divine-mercy-offering", title: "From the Chaplet · offering and response", text: `${CHAPLET_LOCALES.en.prayers.eternal_father}\n\n${CHAPLET_LOCALES.en.prayers.aspiration}`, sourceUrl: "https://www.saint-faustina.org/chaplet-of-the-divine-mercy/", sourceLabel: "Congregation of the Sisters of Our Lady of Mercy · Diary, 476" },
    feast: "Divine Mercy Sunday · the Second Sunday of Easter",
    sources: [
      { label: "St. John Paul II · Dives in Misericordia (1980)", url: "https://www.vatican.va/content/john-paul-ii/en/encyclicals/documents/hf_jp-ii_enc_30111980_dives-in-misericordia.html" },
      { label: "Canonization of St. Faustina (2000)", url: "https://www.vatican.va/content/john-paul-ii/en/homilies/2000/documents/hf_jp-ii_hom_20000430_faustina.html" },
    ],
  },
  "holy-face": {
    eyebrow: "Reparation and adoration",
    title: "The Holy Face of Jesus",
    latin: "Vultus Christi",
    lede: "The face marred in the Passion — wiped by Veronica in the received devotional tradition, veiled and beaten, yet sought in prayer. The Psalmist’s longing to seek the Lord’s face (Psalm 27:8–9) illuminates this devotion.",
    image: { src: `${IMG}/holy-face.webp`, alt: "The Holy Face on the veil of Veronica", credit: "The Holy Face (La sainte face) · James Tissot · Public domain (Brooklyn Museum)" },
    history: [
      "Tradition holds that a woman, Veronica, wiped the face of Christ on the way to Calvary and received its image on her veil. The devotion to the Holy Face grew at Tours in the 19th century through Sr. Marie de St-Pierre and the layman Ven. Léon Papin-Dupont, offered in reparation for blasphemy and the profanation of Sundays.",
      "St. Thérèse of Lisieux took the name 'of the Child Jesus and of the Holy Face,' contemplating the hidden, suffering face of God as the heart of her 'little way.'",
    ],
    graces: {
      title: "Why the Face",
      items: [
        "It is the human face of God, gazing on us with mercy.",
        "It bears the marks of our sins, and asks for reparation.",
        "It is a mirror of the soul of Christ — meek, humble, obedient.",
        "It draws the little and the hidden along St. Thérèse's way.",
      ],
    },
    quotes: [
      { text: "Isaiah portrays the servant as despised, acquainted with suffering and without the beauty that attracts admiration.", author: "Isaiah", kind: "paraphrase", source: "Isaiah 53:2–3 · RSV-CE", url: scriptureLink("Isaiah 53:2-3") },
      { text: scriptureText("Psalm 27:8-9"), author: "The Psalmist", source: "Psalm 27:8–9 · RSV-CE", url: scriptureLink("Psalm 27:8-9") },
    ],
    prayer: GOLDEN_ARROW,
    feast: "Shrove Tuesday · a traditional devotional observance",
    sources: [
      { label: "Catechism §§1159–1162 (holy images)", url: "https://www.vatican.va/archive/ccc_css/archive/catechism/p2s1c1a1.htm" },
    ],
  },
  "eucharistic-miracles": {
    eyebrow: "The Real Presence",
    title: "The Eucharist & its Miracles",
    latin: "Panis Angelicus",
    lede: "The Heart of Jesus does not only appear in visions — it abides, truly, in the Blessed Sacrament. Down the centuries, when faith grew weak, God has confirmed the gift with signs.",
    image: { src: "/images/treasury/carlo-acutis.webp", alt: "St. Carlo Acutis, who catalogued Eucharistic miracles", credit: "St. Carlo Acutis memorial · Corpus Christi Church, London · CC image" },
    history: [
      "The Last Supper accounts identify the bread with Christ’s body and the cup with his blood (Matthew 26:26–28). The Church confesses his true, real, and substantial presence under the appearances of bread and wine — the mystery the Fourth Lateran Council named transubstantiation and the Council of Trent solemnly reaffirmed.",
      "The tradition of the miracle at Bolsena is closely associated with Corpus Christi. The feast’s history also includes St. Juliana of Liège and its local institution in Liège in 1246, before Urban IV extended it to the universal Church in 1264. In our own day, St. Carlo Acutis gathered accounts of Eucharistic miracles into an online exhibition — a direct inspiration for this treasury.",
    ],
    graces: {
      title: "Signs to strengthen faith",
      items: [
        "Lanciano (8th century) — host and wine become flesh and blood.",
        "Bolsena–Orvieto (traditionally 1263) — associated with the spread of Corpus Christi.",
        "Buenos Aires (1996) — a host examined as cardiac tissue.",
        "The catalogue of St. Carlo Acutis — miracles from around the world.",
        "Eucharistic adoration — ‘My Lord and my God!’ (John 20:28, RSV-CE).",
      ],
    },
    quotes: [
      { text: scriptureText("John 6:51"), author: "Jesus in the Gospel of John", source: "John 6:51 · RSV-CE", url: scriptureLink("John 6:51") },
      SAINT_QUOTATIONS.carlo,
    ],
    prayer: { id: "eucharistic-acclamation", title: "Before the Blessed Sacrament · Gospel acclamation", text: scriptureText("John 20:28"), sourceUrl: scriptureLink("John 20:28"), sourceLabel: "John 20:28 · RSV-CE · words of Thomas" },
    feast: "The Solemnity of Corpus Christi · after Trinity Sunday",
    sources: [
      { label: "Catechism §§1373–1381 (the Real Presence)", url: "https://www.vatican.va/archive/ccc_css/archive/catechism/p2s2c1a3.htm" },
    ],
  },
  sovereignty: {
    eyebrow: "The reign of Christ",
    title: "Christ the King",
    latin: "Christus Rex",
    lede: "The pierced Heart is a King's Heart. Christ reigns not by force but by truth and love — over every mind, will, and heart, and over the life of nations.",
    image: { src: "/images/prayer-rope/christ-pantocrator.webp", alt: "Christ Pantocrator, the Ruler of All", credit: "Christ Pantocrator · Mount Sinai, 6th century · Public domain" },
    history: [
      "In 1925, as ideologies rose that would soon deny God, Pope Pius XI instituted the feast of Christ the King in the encyclical Quas Primas — proclaiming that Christ's kingship is not merely spiritual but extends over all things, and that true peace comes only in his reign.",
      "The General Roman Calendar now crowns the liturgical year with this solemnity on the last Sunday before Advent. Hebrews 13:8 confesses the enduring identity of Jesus Christ across yesterday, today and eternity.",
    ],
    graces: {
      title: "Where Christ must reign",
      items: [
        "Over the mind — by faith in the whole truth he revealed.",
        "Over the will — by obedience to his law of love.",
        "Over the heart — by charity above all earthly goods.",
        "Over the home and society — by ordering all to God.",
        "Over history — as its beginning, meaning, and end.",
      ],
    },
    quotes: [
      { text: "Pius XI calls Christians to let Christ reign in their minds, wills, hearts and bodily lives.", author: "Pope Pius XI", kind: "paraphrase", source: "Quas Primas (1925), §33", url: "https://www.vatican.va/content/pius-xi/en/encyclicals/documents/hf_p-xi_enc_11121925_quas-primas.html" },
      { text: scriptureText("Revelation 19:16"), author: "The Book of Revelation", source: "Revelation 19:16 · RSV-CE", url: scriptureLink("Revelation 19:16") },
    ],
    prayer: { id: "our-father", title: "The Lord’s Prayer · Thy kingdom come", text: TRADITIONAL_ENGLISH.our_father, sourceUrl: BALTIMORE_SOURCE, sourceLabel: "Baltimore Catechism no. 2 · The Lord’s Prayer" },
    feast: "The Solemnity of Christ the King · the last Sunday of the liturgical year, before Advent",
    sources: [
      { label: "Pius XI · Quas Primas (1925)", url: "https://www.vatican.va/content/pius-xi/en/encyclicals/documents/hf_p-xi_enc_11121925_quas-primas.html" },
    ],
  },
};

export const TWO_HEARTS = {
  eyebrow: "United in love, united to save",
  title: "The Two Hearts",
  latin: "Cor Iesu · Cor Mariae",
  lede: "One Heart was pierced by a lance; the other by a sword foretold. They are not rivals but one love — the Son who redeems, and the Mother whose whole heart is turned toward him. To honour her Immaculate Heart is to be led straight to his Sacred Heart.",
  hero: { src: `${IMG}/two-hearts.webp`, alt: "The Sacred Heart of Jesus and the Immaculate Heart of Mary, united", credit: "The Two Hearts of Jesus and Mary · devotional artwork provided by the site owner" },
  hearts: [
    {
      key: "jesus",
      title: "The Sacred Heart of Jesus",
      latin: "Cor Iesu Sacratissimum",
      image: { src: `${IMG}/sacred-heart.webp`, alt: "The Sacred Heart of Jesus", credit: "The Sacred Heart of Jesus · devotional artwork provided by the site owner" },
      scripture: `${scriptureText("John 19:37")} — John 19:37 · RSV-CE`,
      text: "Crowned with thorns, wrapped in flame, surmounted by the Cross: the Heart of the Redeemer, opened so that mercy might flow out upon the world.",
    },
    {
      key: "mary",
      title: "The Immaculate Heart of Mary",
      latin: "Cor Mariae Immaculatum",
      image: { src: "/images/apparition-marian/immaculate-heart.webp", alt: "The Immaculate Heart of Mary", credit: "Immaculate Heart of Mary · Diana Ringo · CC BY-SA 4.0" },
      scripture: `${scriptureText("Luke 2:35")} — Luke 2:35 · RSV-CE`,
      text: "Pierced by the sword, encircled by roses: Mary treasures and ponders what she has witnessed (Luke 2:19). Her heart is wholly given to her Son, leading us to him.",
    },
  ],
  quotes: [
    { text: "John Eudes promoted devotion to the Hearts of Jesus and Mary as a school of Christian love and holiness.", author: "St. John Eudes", kind: "paraphrase", source: "Benedict XVI, General audience, 19 August 2009", url: "https://www.vatican.va/content/benedict-xvi/en/audiences/2009/documents/hf_ben-xvi_aud_20090819.html" },
    { text: "My Immaculate Heart will be your refuge and the way that will lead you to God.", author: "Mary, as reported by Lúcia", source: "Fourth Memoir · 13 June 1917 account, p. 177 in the shrine’s cited edition", url: "https://www.fatima.pt/en/pages/narrative-of-the-apparitions-/" },
    SAINT_QUOTATIONS.montfort,
    { text: "Pius XII encourages devotion to Mary’s Immaculate Heart in close union with devotion to the Sacred Heart of Jesus.", author: "Pope Pius XII", kind: "paraphrase", source: "Haurietis Aquas (1956), §124", url: "https://www.vatican.va/content/pius-xii/en/encyclicals/documents/hf_p-xii_enc_15051956_haurietis-aquas.html" },
  ],
  consecration: IMMACULATE_HEART,
  feast: "The Sacred Heart (Friday after Corpus Christi) and the Immaculate Heart (the Saturday following) are joined in the Church's calendar.",
  sources: [
    { label: "Pius XII · Haurietis Aquas (1956)", url: "https://www.vatican.va/content/pius-xii/en/encyclicals/documents/hf_p-xii_enc_15051956_haurietis-aquas.html" },
    { label: "The message of Fátima", url: "https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_con_cfaith_doc_20000626_message-fatima_en.html" },
  ],
};

// Matching printable prayer cards for the Sacred Heart of Jesus and the Immaculate
// Heart of Mary — a pair, in the same layout, for families and parishes.
const HEARTS_FOOTER = "Theotokos · Mater Dei — Ad Iesum per Mariam";

export const TWO_HEARTS_CARDS = [
  {
    key: "sacred-heart",
    heading: "The Sacred Heart of Jesus",
    latin: "Cor Iesu Sacratissimum",
    card: {
      eyebrow: "Cor Iesu Sacratissimum",
      title: "The Sacred Heart of Jesus",
      subtitle: "An act of consecration and love",
      image: { src: `${IMG}/sacred-heart.webp`, alt: "The Sacred Heart of Jesus", credit: "The Sacred Heart of Jesus · devotional artwork provided by the site owner" },
      blocks: [
        { label: SACRED_HEART.title, text: SACRED_HEART.text, italic: true },
      ],
      note: "Solemnity of the Sacred Heart — the Friday after Corpus Christi.",
      source: { label: SACRED_HEART.sourceLabel, url: SACRED_HEART.sourceUrl },
      back: { eyebrow: "Cor Iesu Sacratissimum", name: "The Sacred Heart of Jesus", blessing: "Heart of Jesus, I put my trust in Thee!" },
      footer: HEARTS_FOOTER,
    },
  },
  {
    key: "immaculate-heart",
    heading: "The Immaculate Heart of Mary",
    latin: "Cor Mariae Immaculatum",
    card: {
      eyebrow: "Cor Mariae Immaculatum",
      title: "The Immaculate Heart of Mary",
      subtitle: "An act of consecration",
      image: { src: "/images/apparition-marian/immaculate-heart.webp", alt: "The Immaculate Heart of Mary", credit: "Immaculate Heart of Mary · Diana Ringo · CC BY-SA 4.0" },
      blocks: [
        { label: IMMACULATE_HEART.title, text: IMMACULATE_HEART.text, italic: true },
      ],
      note: "Memorial of the Immaculate Heart — the Saturday after the Sacred Heart.",
      source: { label: IMMACULATE_HEART.sourceLabel, url: IMMACULATE_HEART.sourceUrl },
      back: { eyebrow: "Cor Mariae Immaculatum", name: "The Immaculate Heart of Mary", blessing: "Ave Maria, gratia plena, Dominus tecum." },
      footer: HEARTS_FOOTER,
    },
  },
];
